import type { Page, Pageable } from '../../../core/api/spring-types';
import { normalizePage } from '../../../core/api/spring-types';
import {
    getAllLessons,
    getLessonById,
    createLesson,
    updateLesson,
    deleteLesson,
    getLessonsByType,
    getDefaultLesson,
    hideLesson,
    getLessonAnswersReport,
    confirmLesson,
    getLessonProgressByType,
    createAnswer,
    getAnswersByLesson,
    getAnswersForSubmission,
    getDefaultAnswer,
    getDefaultSlide,
    getSlideById,
    getDefaultOption,
    deleteLessonAnswersByUserLessonAndEdition,
    updateParticipants as updateParticipantsSdk,
    depuneActivitatePractica,
} from '../../../api/generated/sdk.gen';
import type {
    LessonDto,
    AnswerDto,
    LessonAnswerReportDto,
    SlideDto,
    OptionDto
} from '../../../api/generated/types.gen';
import { handleResponse } from '@/core/api/response-handler';

/**
 * Lessons API - Thin wrapper for auto-generated SDK functions
 * Maintains backward compatibility with existing hook signatures
 * Follows the same pattern as schools.api.ts, users.api.ts, waste-collection.api.ts
 */
class LessonsAPI {
    // ===============================
    // Lesson CRUD operations
    // ===============================

    async list(): Promise<LessonDto[]> {
        const response = await getAllLessons();
        return handleResponse(response);
    }

    async listByType(type: string, filters?: Record<string, any>): Promise<LessonDto[]> {
        const response = await getLessonsByType({
            query: { type, ...filters } as any
        });
        return handleResponse(response);
    }

    async getById(id: string): Promise<LessonDto> {
        const response = await getLessonById({
            path: { id }
        });
        return handleResponse(response);
    }

    async create(lesson: LessonDto): Promise<LessonDto> {
        const response = await createLesson({
            body: lesson
        });
        return handleResponse(response);
    }

    async update(lesson: LessonDto): Promise<LessonDto> {
        const response = await updateLesson({
            body: lesson
        });
        return handleResponse(response);
    }

    async delete(id: string): Promise<void> {
        const response = await deleteLesson({
            path: { id }
        });
        handleResponse(response);
    }

    // ===============================
    // Special lesson operations
    // ===============================

    async toggleVisibility(id: string, isHidden: boolean): Promise<void> {
        const response = await hideLesson({
            path: { id },
            query: { isHidden }
        });
        handleResponse(response);
    }

    async getDefaultLesson(): Promise<LessonDto> {
        const response = await getDefaultLesson();
        return handleResponse(response);
    }

    // ===============================
    // Answer operations
    // ===============================

    async submitAnswer(answer: AnswerDto): Promise<AnswerDto> {
        const response = await createAnswer({
            body: answer
        });
        return handleResponse(response);
    }

    async getAnswersForLesson(lessonId: string): Promise<AnswerDto[]> {
        const response = await getAnswersByLesson({
            path: { lessonId }
        });
        return handleResponse(response);
    }

    /**
     * Get answers for current user's lesson attempt (student workflow)
     * Uses JWT token to identify user automatically
     *
     * Backend endpoint: GET /api/answers/lessons/{lessonId}
     * User is automatically determined from JWT token in request headers
     *
     * Use this for: Students viewing their own progress/resuming lessons
     */
    async getAnswersForCurrentUser(lessonId: string): Promise<AnswerDto[]> {
        const response = await getAnswersByLesson({
            path: { lessonId }
        });
        return handleResponse(response);
    }

    /**
     * Get answers for a specific submission (lesson answer attempt)
     * Used when activities allow multiple attempts (e.g. PRACTICAL_ACTIVITY) — scopes
     * the fetch on the submission itself, not on (lesson, user), so it doesn't mix
     * different attempts of the same activity.
     *
     * Backend endpoint: GET /api/lessons/answers/{id}/answers
     */
    async getAnswersForSubmission(lessonAnswerId: string): Promise<AnswerDto[]> {
        const response = await getAnswersForSubmission({
            path: { id: lessonAnswerId }
        });
        return handleResponse(response);
    }

    async getDefaultAnswer(): Promise<AnswerDto> {
        const response = await getDefaultAnswer();
        return handleResponse(response);
    }

    // ===============================
    // Slide operations
    // ===============================

    async getDefaultSlide(): Promise<SlideDto> {
        const response = await getDefaultSlide();
        return handleResponse(response);
    }

    async getSlideById(id: string): Promise<SlideDto> {
        const response = await getSlideById({
            path: { id }
        });
        return handleResponse(response);
    }

    // ===============================
    // Option operations
    // ===============================

    async getDefaultOption(): Promise<OptionDto> {
        const response = await getDefaultOption();
        return handleResponse(response);
    }

    // ===============================
    // Reporting operations
    // ===============================

    /**
     * Get lesson answers report with pagination
     * PRIMARY USE CASE - Report page endpoint
     */
    async getAnswersReport(
        filter: Partial<LessonAnswerReportDto>,
        pageable: Pageable
    ): Promise<Page<LessonAnswerReportDto>> {
        const response = await getLessonAnswersReport({
            query: { ...filter, ...pageable } as any,
        });
        return normalizePage(handleResponse(response));
    }

    async updateApproval(
        id: string,
        isApproved?: boolean,
        rejectReason?: string,
        points?: number,
        fise?: number
    ): Promise<void> {
        const query: any = {};
        if (typeof isApproved === 'boolean') {
            query.isApproved = isApproved;
        }
        if (rejectReason) {
            query.rejectReason = rejectReason;
        }
        if (typeof points === 'number') {
            query.points = points;
        }
        // SE-32/33: CLASS_EXERCISE trimite `fise` in loc de `points` — backendul calculeaza
        // 8 x fise si respinge cu 400 daca vin amandoua.
        if (typeof fise === 'number') {
            query.fise = fise;
        }

        const response = await confirmLesson({
            path: { id },
            query
        });

        handleResponse(response);
    }

    async getLessonProgress(type?: string, schoolId?: string): Promise<any[]> {
        const query: any = {};
        if (type) query.type = type;
        if (schoolId) query.schoolId = schoolId;

        const response = await getLessonProgressByType({
            query
        });
        return handleResponse(response);
    }

    /**
     * B3: profesorul depune dovada unei activitati practice intr-un singur apel (poze + clase +
     * descriere). Autorul, scoala si editia vin din JWT, nu din corp. Refuzurile (400 poze /
     * fragmenteNerecunoscute, 409 termen / scoala / activitate ascunsa) ies ca ApiValidationError.
     */
    async submitPracticalEvidence(
        lessonId: string,
        body: { photoUrls: string[]; clase?: string; description?: string }
    ): Promise<{ lessonAnswerId: string }> {
        const response = await depuneActivitatePractica({
            path: { id: lessonId },
            body
        });

        const data = handleResponse(response);
        return { lessonAnswerId: data.lessonAnswerId };
    }

    /**
     * Delete lesson answers for specific user, lesson, and edition
     * Used by ADMIN/DBEST to remove invalid submissions or reset stuck attempts
     * Deletes the LessonAnswer aggregate and all related Answer records
     */
    async deleteAnswer(userId: string, lessonId: string, editionId: string): Promise<void> {
        const response = await deleteLessonAnswersByUserLessonAndEdition({
            path: { userId, lessonId, editionId }
        });

        handleResponse(response);
    }

    /**
     * Update participants count for a CLASS_EXERCISE lesson answer
     * Only works for CLASS_EXERCISE type, before approval (PENDING_APPROVAL state)
     * Professors can only update their own school's answers
     * ADMIN/DBEST can update any school
     *
     * @param lessonAnswerId - ID of the lesson answer to update
     * @param participants - Number of participants (must be >= 1)
     * @returns Promise<void>
     */
    async updateParticipants(lessonAnswerId: string, participants: number): Promise<void> {
        const response = await updateParticipantsSdk({
            path: { id: lessonAnswerId },
            query: { participants }
        });

        handleResponse(response);
    }
}

// Export singleton instance
export const lessonsAPI = new LessonsAPI();

// Re-export types for convenience
export type {
    LessonDto,
    AnswerDto,
    LessonAnswerReportDto,
    SlideDto,
    OptionDto
};
